/**
 * Team → Client briefs — prototype `VIEWS.briefs` (docs/prototype.html 3659): a card per client (logo, name,
 * SPOC, priority; the brief line; a chip per product with its utilisation; the engagement chip; "Next:" the
 * first idea), worst health first. "Copy all as a message" is in the head. Pressing a card opens the brief.
 *
 * Data: GET /briefs (managers, `team.read`); product chip colours use each product's low and watch lines from
 * GET /products, as the prototype's `PRODUCTS[p].low / .watch`.
 */
import { useMemo } from 'react';
import { useSearchParams } from 'react-router';
import type { BriefCard } from '@amplispot/contracts';
import { overdueTone, useBriefs } from '../../lib/engagement';
import { firstName } from '../../lib/format';
import { toneOf, useProducts } from '../../lib/usage';
import { Empty, ErrorState, Loading, PriorityChip, useToast } from '../../ui/kit';
import { ClientLogo } from '../clients/parts';
import { copyText } from '../timesheets/TsModals';

const clip = (s: string, n: number) => (s.length > n ? s.slice(0, n - 1).trim() + '…' : s);

/** The plain-text message for one card (the cards carry no revenue, so neither does this). */
const cardText = (c: BriefCard) =>
  [`*${c.client.name}* (SPOC ${firstName(c.client.spoc.name)})`, c.line, c.next ? `Next: ${c.next}` : ''].filter(Boolean).join('\n');

export function Briefs() {
  const q = useBriefs({});
  const products = useProducts();
  const [, setSp] = useSearchParams();
  const toast = useToast();
  const lines = useMemo(() => new Map((products.data ?? []).map((p) => [p.key, p])), [products.data]);

  const open = (id: string) =>
    setSp((p) => {
      const n = new URLSearchParams(p);
      n.set('brief', id);
      return n;
    });

  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const cards = q.data?.data ?? [];

  return (
    <>
      <div className="ph" style={{ margin: '0 0 12px', justifyContent: 'flex-end' }}>
        <div className="r" style={{ marginLeft: 0 }}>
          <button className="btn" disabled={!cards.length} onClick={() => copyText(cards.map(cardText).join('\n\n'), () => toast('Copied. Paste it into WhatsApp.'))}>
            Copy all as a message
          </button>
        </div>
      </div>
      {cards.length ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(min(340px,100%),1fr))', gap: 14 }}>
          {cards.map((c) => (
            <div
              key={c.client.id}
              className="panel click"
              style={{ cursor: 'pointer' }}
              role="button"
              tabIndex={0}
              onClick={() => open(c.client.id)}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), open(c.client.id))}
              aria-label={`Open the brief for ${c.client.name}`}
            >
              <div className="bd">
                <div className="split" style={{ marginBottom: 8 }}>
                  <ClientLogo id={c.client.id} name={c.client.name} size={38} />
                  <div className="grow" style={{ minWidth: 0 }}>
                    <b style={{ fontSize: 15 }}>{c.client.name}</b>
                    <div className="sub">SPOC {firstName(c.client.spoc.name)}</div>
                  </div>
                  <PriorityChip p={c.client.priority} />
                </div>
                <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--sub)', marginBottom: 10 }}>{c.line}</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {c.products.map((p) => {
                    const pr = lines.get(p.product_key);
                    const tone = p.util == null ? 'info' : pr ? toneOf(p.util, pr.low_line, pr.watch_line) : '';
                    return (
                      <span key={p.product_key} className={`chip ${tone}`}>
                        {p.name} {p.util == null ? '-' : `${Math.round(p.util)}%`}
                      </span>
                    );
                  })}
                  <span className={`chip ${overdueTone(c.engagement.total - c.engagement.on_track)}`}>
                    Engagement {c.engagement.on_track}/{c.engagement.total}
                  </span>
                </div>
                {c.next ? (
                  <div className="sub" style={{ marginTop: 10 }}>
                    <b>Next:</b> {clip(c.next, 110)}
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="panel">
          <Empty title="No clients yet">A brief appears here for each active client once it is added to the client book.</Empty>
        </div>
      )}
    </>
  );
}
